(() => {
  const NAME = 'product-card-image-hover';
  const HOVER_QUERY = '(any-hover: hover) and (any-pointer: fine)';
  const PREVIEW_DRAG_THRESHOLD = 10;
  if (typeof customElements === 'undefined' || customElements.get(NAME)) return;

  // A temporary photo preview, independent of Embla's selected slide and gestures.
  class ProductCardImageHover extends HTMLElement {
    constructor() {
      super();
      this.sequence = 0;
      this.images = new Map();
      this.showPreview = this.showPreview.bind(this);
      this.hidePreview = this.hidePreview.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.clearPointer = this.clearPointer.bind(this);
      this.handleHoverChange = this.handleHoverChange.bind(this);
    }

    connectedCallback() {
      if (this.surface) return;
      this.surface = this.parentElement;
      this.card = this.closest(
        'collection-product-card, homepage-collection-product-card, '
        + '.legacy-home-feature__product, .legacy-product-card, .legacy-product-editorial__card, .card-wrapper',
      ) || this.surface;
      this.hoverQuery = window.matchMedia(HOVER_QUERY);
      try {
        this.manifest = JSON.parse(this.querySelector('script')?.textContent || 'null');
      } catch (_error) {
        this.manifest = null;
      }
      this.surface.addEventListener('pointerenter', this.showPreview);
      this.surface.addEventListener('pointerleave', this.hidePreview);
      this.card.addEventListener('pointerdown', this.handlePointerDown, true);
      this.card.addEventListener('pointermove', this.handlePointerMove);
      this.card.addEventListener('pointerup', this.clearPointer);
      this.card.addEventListener('pointercancel', this.clearPointer);
      this.card.addEventListener('wheel', this.hidePreview, { passive: true });
      this.card.addEventListener('keydown', this.hidePreview);
      this.hoverQuery.addEventListener('change', this.handleHoverChange);
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => (
          record.type === 'attributes' || record.removedNodes.length > 0
        ))) {
          this.hidePreview();
          if (this.nearViewport) this.warmPreview();
        }
      });
      this.observer.observe(this.card, {
        attributes: true,
        attributeFilter: ['data-selected-variant-id', 'src', 'srcset'],
        childList: true,
        subtree: true,
      });
      this.observeWarmup();
    }

    disconnectedCallback() {
      this.hidePreview();
      this.surface?.removeEventListener('pointerenter', this.showPreview);
      this.surface?.removeEventListener('pointerleave', this.hidePreview);
      this.card?.removeEventListener('pointerdown', this.handlePointerDown, true);
      this.card?.removeEventListener('pointermove', this.handlePointerMove);
      this.card?.removeEventListener('pointerup', this.clearPointer);
      this.card?.removeEventListener('pointercancel', this.clearPointer);
      this.card?.removeEventListener('wheel', this.hidePreview);
      this.card?.removeEventListener('keydown', this.hidePreview);
      this.hoverQuery?.removeEventListener('change', this.handleHoverChange);
      this.observer?.disconnect();
      this.warmupObserver?.disconnect();
      this.warmupObserver = null;
      this.nearViewport = false;
      this.surface = null;
    }

    handleHoverChange() {
      this.hidePreview();
      this.observeWarmup();
    }

    observeWarmup() {
      if (this.warmupObserver || !this.hoverQuery.matches || navigator.connection?.saveData
        || typeof IntersectionObserver === 'undefined'
        || !this.card.matches('.legacy-product-card, .legacy-product-editorial__card')) return;
      // Prepare only nearby Legacy cards; touch devices keep the primary photo.
      this.warmupObserver = new IntersectionObserver((entries) => {
        this.nearViewport = entries.some((entry) => entry.isIntersecting);
        if (this.nearViewport) this.warmPreview();
      }, { rootMargin: '240px' });
      this.warmupObserver.observe(this.surface);
    }

    warmPreview() {
      if (!this.hoverQuery.matches || navigator.connection?.saveData) return;
      const media = this.mediaForHover();
      if (media?.src) this.loadImage(media).catch(() => {});
    }

    handlePointerDown(event) {
      this.clearPointer();
      if (
        event.pointerType !== 'mouse' || event.button !== 0
        || !this.surface.contains(event.target)
        || event.target.closest('button, input, select, textarea')
      ) {
        this.hidePreview();
        return;
      }
      this.pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }

    handlePointerMove(event) {
      if (!event.buttons) return this.clearPointer();
      if (!this.pointerStart || event.pointerId !== this.pointerStart.id) return;
      if (Math.hypot(event.clientX - this.pointerStart.x, event.clientY - this.pointerStart.y) > PREVIEW_DRAG_THRESHOLD) {
        // Dismiss only the photo; Embla still owns drag and click arbitration.
        this.hidePreview();
      }
    }

    clearPointer() {
      this.pointerStart = null;
    }

    mediaForHover() {
      if (this.card.matches('collection-product-card, homepage-collection-product-card')) {
        const manifest = this.card.galleryManifest;
        const variantId = this.card.currentVariantId || manifest?.displayVariantId;
        const media = manifest?.desktop?.variants?.[variantId]?.media?.[1];
        if (media) return media;
      }
      const variantId = this.card.currentVariantId
        || this.card.dataset.selectedVariantId
        || this.manifest?.initialVariantId;
      return this.manifest?.variants?.[variantId] || null;
    }

    imageUrl(source, width) {
      const url = new URL(source, window.location.href);
      if (
        url.protocol !== 'https:'
        || url.username || url.password
        || (url.origin !== window.location.origin && url.hostname !== 'cdn.shopify.com')
      ) throw new Error('Invalid product image');
      url.searchParams.set('width', String(width));
      return url.href;
    }

    loadImage(media) {
      const width = Number(media.width);
      if (!Number.isFinite(width) || width <= 0) return Promise.reject(new Error('Invalid image width'));
      const sizes = this.surface.querySelector('img')?.sizes
        || `${Math.ceil(this.surface.getBoundingClientRect().width) || 533}px`;
      const key = `${media.src}|${sizes}`;
      if (this.images.has(key)) return this.images.get(key);
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = 'low';
      image.sizes = sizes;
      const widths = [180, 360, 533, 720, 940].filter((candidate) => candidate <= width);
      if (!widths.length) widths.push(width);
      const promise = new Promise((resolve, reject) => {
        image.onload = async () => {
          try {
            if (typeof image.decode === 'function') await image.decode();
            if (image.naturalWidth <= 0) throw new Error('Empty product image');
            resolve(image);
          } catch (error) {
            reject(error);
          } finally {
            image.onload = null;
            image.onerror = null;
          }
        };
        image.onerror = () => {
          image.onload = null;
          image.onerror = null;
          reject(new Error('Product image failed to load'));
        };
        image.srcset = widths.map((candidate) => `${this.imageUrl(media.src, candidate)} ${candidate}w`).join(', ');
        image.src = this.imageUrl(media.src, Math.min(width, 533));
      });
      this.images.set(key, promise);
      promise.catch(() => this.images.delete(key));
      return promise;
    }

    async showPreview(event) {
      if (event.pointerType !== 'mouse' || event.buttons || !this.hoverQuery.matches) return;
      const media = this.mediaForHover();
      if (!media?.src) return;
      const sequence = ++this.sequence;
      try {
        const image = await this.loadImage(media);
        if (!this.isConnected || sequence !== this.sequence || this.mediaForHover()?.src !== media.src) return;
        this.style.setProperty('--product-card-hover-image', `url(${JSON.stringify(image.currentSrc || image.src)})`);
        this.dataset.ready = 'true';
      } catch (_error) {
        // Keep the current photo when an alternate cannot load or decode.
      }
    }

    hidePreview() {
      this.sequence += 1;
      this.clearPointer();
      delete this.dataset.ready;
    }
  }

  customElements.define(NAME, ProductCardImageHover);
})();
